/**
 * triageHelpers.ts — CVG6-WEBTRIAGE (L137 + L138 + L142, 2026-07-13)
 * ────────────────────────────────────────────────────────────────────────────
 * Pure helpers for the Lead Triage Dashboard:
 *   L137 — State→City cascade + triage-create payload (incl. the
 *          "Other / not listed" free-text escape),
 *   L138 — member-picker filtering + the pickAssignee-contract write body
 *          (executive rows send chosenUserId; "team manager" rows send NONE),
 *   L142 — knob-driven hot-overdue copy (NO hardcoded hour values).
 */

// ─── L137: geo cascade ───────────────────────────────────────────────────────

export interface GeoState {
  name: string;
  cities: string[];
}

/** The free-text escape row appended to every city list (walk-ins from
 *  unlisted villages must never be blocked). */
export const OTHER_CITY_OPTION = 'Other / not listed';

/** Case-insensitive substring filter; blank query → the full list. */
export function filterByQuery(items: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((i) => i.toLowerCase().includes(q));
}

/** Cities of the chosen state (empty when the state is unknown/unpicked). */
export function citiesForState(states: GeoState[], stateName: string): string[] {
  const s = states.find((x) => x.name === stateName);
  return s ? s.cities : [];
}

export interface TriageCreateInput {
  phone: string;
  name: string;
  state: string;
  city: string;
  otherCity: string;
  productInterest: string;
  source: string;
}

/**
 * POST /lead-capture/triage-create payload. City stays in the existing
 * `location` field format; `state` rides additively. Choosing the
 * OTHER_CITY_OPTION routes the free-text city into `location`.
 */
export function buildTriageCreatePayload(input: TriageCreateInput): Record<string, unknown> {
  const city = input.city === OTHER_CITY_OPTION ? input.otherCity.trim() : input.city.trim();
  return {
    phone: input.phone,
    name: input.name.trim(),
    location: city || null,
    state: input.state.trim() || null,
    productInterest: input.productInterest || null,
    source: input.source,
  };
}

// ─── L138: member picker ─────────────────────────────────────────────────────

export interface AssignableMember {
  userId: string;
  name: string;
  teamId: string;
  teamName: string;
  role: 'manager' | 'executive';
  openHotCount: number;
}

/** Type-ahead over name + team tag (case-insensitive). */
export function filterMembers(members: AssignableMember[], query: string): AssignableMember[] {
  const q = query.trim().toLowerCase();
  if (!q) return members;
  return members.filter(
    (m) => m.name.toLowerCase().includes(q) || m.teamName.toLowerCase().includes(q),
  );
}

/** Live load-hint label: `N HOT ticking`. */
export function memberLoadHint(openHotCount: number): string {
  return `${openHotCount} HOT ticking`;
}

/**
 * pickAssignee CONTRACT — EVERY row sends its own userId as assignToUserId,
 * manager rows included.
 *
 * 🔒 2026-08-17 (Manak). The manager row used to send NO assignToUserId and
 * lean on the backend's blank-pick manager fallback. That fallback was
 * REMOVED on 2026-08-10 (a machine choosing owners dumped 120 leads on Dixit
 * in an hour), and the 2026-08-14 ownerless guard then rejects every blank
 * pick with 400 "This team has no manager to receive the lead". Net effect:
 * the "Team manager" row was dead on every team, and a TEAM_HEAD who is not
 * also a FIELD_EXECUTIVE (Mamta on Panipat) was unreachable from triage.
 *
 * Sending the userId makes the coordinator's click an EXPLICIT human choice —
 * pickAssignee returns mode 'executive', the lead gets a real owner, and the
 * routing trace records ownerChoice 'explicit' (which is now the truth). No
 * blank pick is ever sent, so the 2026-08-10 auto-dump cannot return.
 *
 * Safe for every team: pickAssignee validates the id against the roster's
 * `assignable` list (TRIAGE_ASSIGNABLE_LEVELS = TEAM_HEAD / FIELD_EXECUTIVE /
 * BACKEND_STAFF) and all five live Sun Stellar team managers hold a TEAM_HEAD
 * membership row on the team they manage.
 */
export function assignBodyForSelection(m: AssignableMember): { teamId: string; assignToUserId?: string } {
  return { teamId: m.teamId, assignToUserId: m.userId };
}

// ─── L142: knob-driven copy ──────────────────────────────────────────────────

/**
 * Hot-overdue strip/tab copy. The hour value comes ONLY from the tenant
 * `hotSlaHours` knob (served as `slaHours` by /lead-capture/triage/hot-overdue).
 * Unknown knob → hour-free copy (never a hardcoded number).
 */
export function hotOverdueCopy(slaHours: number | null | undefined): string {
  return typeof slaHours === 'number' && Number.isFinite(slaHours)
    ? `Not called within ${slaHours} working hours. Assign now.`
    : 'Not called within the HOT SLA window. Assign now.';
}

// ─── L238: dealer picker (Round 6 Wave A, 2026-09-03) ────────────────────────
//
// GET /leads/triage/assignable-dealers → { success, groups: AssignableDealerGroup[] }
// (the managerUserId=null "Unassigned" group is always last). Assigning a
// dealer is IN ADDITION to the rep — the rep stays on the lead; the dealer is
// set as the fulfilling dealer. The picker calls `pickDealer(dealer, group)`;
// the parent owns the write and the inline POST /dealers (`createDealer`).

export interface AssignableDealer {
  id: string;
  name: string;
  city: string | null;
  phone: string | null;
  openLeads: number;
  /** SUN-226 — owning manager's first name (row chip); null = company-owned. */
  managerFirstName?: string | null;
}

export interface AssignableDealerGroup {
  managerUserId: string | null;
  managerName: string | null;
  dealers: AssignableDealer[];
}

/**
 * Case-insensitive; matches dealer name/city OR the manager name. Groups with
 * zero matching dealers are dropped, unless the query matches the manager
 * name (then the whole group stays). Blank query → the input array as-is.
 */
export function filterDealerGroups(groups: AssignableDealerGroup[], query: string): AssignableDealerGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;
  const out: AssignableDealerGroup[] = [];
  for (const g of groups) {
    if ((g.managerName ?? '').toLowerCase().includes(q)) {
      out.push(g);
      continue;
    }
    const dealers = g.dealers.filter(
      (d) => d.name.toLowerCase().includes(q) || (d.city ?? '').toLowerCase().includes(q),
    );
    if (dealers.length) out.push({ ...g, dealers });
  }
  return out;
}

/** '3 open leads' / '1 open lead' / '0 open leads'. */
export function dealerLoadHint(n: number): string {
  return `${n} open ${n === 1 ? 'lead' : 'leads'}`;
}

/** "Mamta Aggarwal's dealers (4)" / "Unassigned dealers (4)". */
export function dealerGroupLabel(g: AssignableDealerGroup): string {
  const n = g.dealers.length;
  return g.managerUserId && g.managerName
    ? `${g.managerName}'s dealers (${n})`
    : `Unassigned dealers (${n})`;
}

/** First token of the manager's name ("Mamta Aggarwal" → "Mamta"); null → "". */
export function managerFirstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

export interface DealerQuickAddInput {
  name: string;
  phone: string;
  pincode: string;
  gstin: string;
}

/**
 * Client-side mirror of the POST /dealers quick-add rules (name ≥ 2 chars,
 * phone 10 digits, pincode 6 digits, gstin 15 chars or blank). Returns a map
 * of field → message; empty object = valid.
 */
export function dealerQuickAddErrors(input: DealerQuickAddInput): Partial<Record<keyof DealerQuickAddInput, string>> {
  const errors: Partial<Record<keyof DealerQuickAddInput, string>> = {};
  if (input.name.trim().length < 2) errors.name = 'Name must be at least 2 characters.';
  if (!/^\d{10}$/.test(input.phone.trim())) errors.phone = 'Phone must be 10 digits.';
  if (!/^\d{6}$/.test(input.pincode.trim())) errors.pincode = 'Pincode must be 6 digits.';
  const gstin = input.gstin.trim();
  if (gstin && gstin.length !== 15) errors.gstin = 'GSTIN must be 15 characters or blank.';
  return errors;
}

/** New array with `dealer` inserted, ordered by name (case-insensitive). Input untouched. */
export function insertDealerSorted(dealers: AssignableDealer[], dealer: AssignableDealer): AssignableDealer[] {
  return [...dealers, dealer].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
}
